import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Tv, Film, BarChart3, Sparkles, MapPin, Smartphone, ShieldCheck } from 'lucide-react';

const problems = [
  {
    icon: <Tv className="w-8 h-8" />,
    title: '86% of US adults now stream',
    desc: "Your clients' customers spend hours daily on CTV — but most SMB agencies still can't put them there. (Leichtman Research, 2024)"
  },
  {
    icon: <Film className="w-8 h-8" />,
    title: 'Production stopped them',
    desc: 'Traditional TV ads cost $10K–$50K. MarqiCast generates broadcast-quality spots in minutes for a fraction of that.'
  },
  {
    icon: <BarChart3 className="w-8 h-8" />,
    title: 'No proof, no budget growth',
    desc: "Agencies lose CTV clients because they can't show what it did. MarqiCast measures everything — foot traffic, web visits, ROAS."
  }
];

const features = [
  { icon: <Sparkles className="w-6 h-6" />, title: 'AI Video Creative', desc: 'Broadcast-ready 30-second spots generated from a URL or brief — no production team, no studio.' },
  { icon: <MapPin className="w-6 h-6" />, title: 'Hyperlocal Targeting', desc: 'DMA, city, zip, or 1-mile radius. Audience, income, intent, and contextual signals. Built for local SMBs.' },
  { icon: <BarChart3 className="w-6 h-6" />, title: 'Measurement & Proof', desc: 'Foot traffic lift, website attribution, incremental reach. Reports your clients actually understand.' },
  { icon: <Tv className="w-6 h-6" />, title: 'Premium CTV Inventory', desc: 'Disney+, Hulu, Peacock, Paramount+, Tubi and more. 40M+ households. Brand-safe environments only.' },
  { icon: <Smartphone className="w-6 h-6" />, title: 'Second Screen Retargeting', desc: 'CTV ad seen → mobile follow-up within hours. The most powerful sequence in modern advertising.' },
  { icon: <ShieldCheck className="w-6 h-6" />, title: 'White-Label Everything', desc: 'Your logo. Your colors. Your agency presents the results. Your clients never know who powers it.' }
];

const whyNow = [
  {
    title: 'The gap closes fast, not slowly',
    desc: 'Once a handful of agencies in any category start offering CTV, it stops being a differentiator and becomes the baseline. The agencies who move now are the ones their competitors end up copying.'
  },
  {
    title: "Your clients don't need to ask for it",
    desc: 'Most SMB owners have never considered TV because no one told them they could afford it. The agencies who bring it to them first get credited with the idea — and the budget that follows.'
  }
];

const steps = [
  { num: '01', title: 'Agency Onboarding', desc: 'We set up your white-label account, configure your branding, and walk through your first client briefing together. One session — done.', when: 'Day 1–2' },
  { num: '02', title: 'Creative & Strategy', desc: 'We brief the AI creative tool, generate ad options for your client, and finalise audience targeting and flight strategy together. You review and approve before anything goes live. Our specialists manage the full execution — you stay in control of the client relationship.', when: 'Day 3–5' },
  { num: '03', title: 'Live & Reporting', desc: 'Campaign goes live on premium streaming inventory. Branded reports delivered weekly. Optimisation recommendations monthly.', when: 'Day 7+' }
];

const trust = [
  { title: 'Your Brand, Fully White-labeled', desc: 'Dashboards, reports, and all client communications. Your logo everywhere. Clients associate CTV with your agency.' },
  { title: 'Zero Infrastructure', desc: 'No DSP seat to buy. No creative team to hire. No new platform contracts. Launch your first campaign in days, not months.' },
  { title: 'Revenue You Keep', desc: 'Platform fee from $500/mo (covers AI creative & management). You add media budget and mark it up 25–40%. Example: a $2,000/mo client media budget at 30% = $600 margin per client, per month — before your management fee.' },
  { title: 'Clients That Stay', desc: 'Agencies offering TV stay on retainer longer. CTV clients are harder to poach — they associate you with TV, which feels premium.' }
];

const CastOverviewPage = () => {
  return (
    <div className="min-h-screen" data-testid="cast-overview-page">
      <Helmet>
        <title>MarqiCast — White-Label CTV for Agencies</title>
      </Helmet>

      {/* Hero */}
      <section className="py-24 md:py-32 text-center">
        <div className="container max-w-4xl">
          <h1 className="display-md text-[var(--text-primary)] mb-6" data-testid="cast-hero-title">
            Your Clients’ Next Customer<br />
            Sees Them on <span className="text-[var(--accent-primary)]">TV.</span><br />
            Then Searches to Confirm.
          </h1>
          <p className="body-lg mb-12 max-w-3xl mx-auto">
            Discovery doesn’t always start with a search box. It starts the moment someone sees a brand
            mid-show on Hulu or Peacock — before they’ve thought to look for it. Marqi Cast is how your
            agency puts SMB clients there: AI-generated creative, premium streaming inventory, and proof it
            worked. Fully white-labeled, without hiring a single person.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="cast-hero-cta-primary">
              Book a Strategy Session — Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <a href="#how-it-works" className="btn-secondary">See How It Works</a>
          </div>
        </div>
      </section>

      {/* Above-the-fold offer band */}
      <section className="pb-8 -mt-8">
        <div className="container">
          <div className="feature-card max-w-4xl mx-auto text-center border-t-2 border-t-[var(--accent-primary)]" data-testid="cast-offer-band">
            <p className="body-md text-[var(--text-secondary)]">
              <span className="text-[var(--text-primary)] font-semibold">Zero-risk start:</span> we'll pick a
              client together, build the audience, and generate a sample AI spot — before you spend a dollar.{' '}
              <Link to="/contact" className="text-[var(--accent-primary)] hover:underline">Book your session →</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-20 bg-[var(--bg-secondary)]">
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16 max-w-3xl mx-auto">
            Your Clients’ Audience is Watching TV. Your Agency Isn’t There Yet.
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {problems.map((p, i) => (
              <div key={p.title} className="feature-card border-t-2 border-t-[var(--accent-primary)] hover-lift animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="text-[var(--accent-primary)] mb-6">{p.icon}</div>
                <h3 className="h3 text-[var(--text-primary)] mb-4">{p.title}</h3>
                <p className="body-md">{p.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center body-md mt-12 max-w-2xl mx-auto text-[var(--text-secondary)]">
            This is one half of where discovery happens now. The other half is AI search —{' '}
            <Link to="/rank" className="text-[var(--accent-primary)] hover:underline">see Marqi Rank</Link>.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-24">
        <div className="container">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">One Partner. Every CTV Capability Your Agency Needs.</h2>
            <p className="body-lg">Fully white-labeled. Your brand on every report, every dashboard, every client conversation.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <div key={f.title} className="feature-card hover-lift animate-fade-in" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="w-12 h-12 rounded-full bg-[var(--accent-bg)] flex items-center justify-center text-[var(--accent-primary)] mb-6">{f.icon}</div>
                <h3 className="h3 text-[var(--text-primary)] mb-3">{f.title}</h3>
                <p className="body-md">{f.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center body-md mt-12 max-w-2xl mx-auto text-[var(--text-secondary)]" data-testid="cast-ai-video-callout">
            Want to see what an AI-generated TV spot looks like for a local business? We'll generate one for
            your client — free — in your strategy session. No commitment required.
          </p>
        </div>
      </section>

      {/* Why CTV, Why Now */}
      <section className="py-24 bg-[var(--bg-secondary)]">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <h2 className="h1 text-[var(--text-primary)] mb-6">Right Now, Almost None of Your Clients’ Competitors Are on TV.</h2>
              <p className="body-lg">
                CTV used to be reserved for national brands with six-figure production budgets. That barrier
                is gone — but most SMB agencies haven’t realized it yet, and most SMB businesses have never
                been offered it.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-10 mb-12">
              {whyNow.map((w) => (
                <div key={w.title}>
                  <div className="w-10 h-1 bg-[var(--accent-primary)] mb-5"></div>
                  <h3 className="h3 text-[var(--text-primary)] mb-3">{w.title}</h3>
                  <p className="body-md">{w.desc}</p>
                </div>
              ))}
            </div>

            <p className="text-center body-md max-w-2xl mx-auto text-[var(--text-secondary)] pt-8 border-t border-[var(--border-subtle)]">
              Every MarqiCast campaign is delivered white-label, with no infrastructure or media-buying
              experience required from your team.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-24">
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16 max-w-3xl mx-auto">From Signed Agreement to Live Campaign — In Days.</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <div key={s.num} className="feature-card animate-fade-in" style={{ animationDelay: `${i * 0.15}s` }}>
                <div className="w-16 h-16 rounded-full bg-[var(--accent-bg)] flex items-center justify-center mb-6">
                  <span className="text-2xl font-bold text-[var(--accent-primary)]">{s.num}</span>
                </div>
                <h3 className="h2 text-[var(--text-primary)] mb-3">{s.title}</h3>
                <p className="body-md mb-6">{s.desc}</p>
                <div className="inline-block px-3 py-1 rounded-full bg-[var(--accent-bg)] text-[var(--accent-primary)] text-sm font-semibold">{s.when}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why partner */}
      <section className="py-24 bg-[var(--bg-secondary)]">
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16 max-w-3xl mx-auto">Everything You Need to Offer CTV. Nothing You Don’t.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {trust.map((t, i) => (
              <div key={t.title} className="animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="w-10 h-1 bg-[var(--accent-primary)] mb-6"></div>
                <h3 className="h3 text-[var(--text-primary)] mb-4">{t.title}</h3>
                <p className="body-md">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24">
        <div className="container">
          <div className="feature-card text-center max-w-4xl mx-auto" style={{ background: 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--accent-bg) 100%)' }}>
            <h2 className="h1 text-[var(--text-primary)] mb-6">Let’s Put Your First Client on TV.</h2>
            <p className="body-lg mb-12 max-w-3xl mx-auto">
              Book a complimentary strategy session. We’ll pick a client together, build the audience,
              generate the creative, and show you exactly what the campaign would look like — before
              spending a dollar.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="btn-primary glow-effect" data-testid="cast-final-cta-primary">
                Book Your Strategy Session
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link to="/cast/pricing" className="btn-secondary">View Pricing</Link>
            </div>
            <p className="body-md mt-10 text-[var(--text-muted)]">
              Want to cover AI search too?{' '}
              <Link to="/rank" className="text-[var(--accent-primary)] hover:underline">See Marqi Rank</Link>{' '}
              — the other half of how your clients get found.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CastOverviewPage;
